import { useState, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { Element as ScrollElement, scroller } from 'react-scroll'
import { useRouter } from 'next/router'
import Link from 'next/link'
import DeMinimis from '../faq/DeMinimis'
import RestrictedProducts from '../faq/RestrictedProducts'
import getFaqInter from '../faq/faqInter'
import getFaqLocal from '../faq/faqLocal'
import NewShell from './NewShell'
import styles from './FaqNew.module.scss'

// NEW-design (?new) variant of pages/faq.js, rebuilt to the FAQ mockup
// (new-design-faq-login, 1920x3430): full-bleed city/water hero with an
// overlapping search box, a sticky category sidebar, and category-grouped
// accordions. The Q&A CONTENT is the SAME real data the old FaqPage uses
// (getFaqLocal / getFaqInter) — including the embedded YouTube video that
// lives inside the first answer — so nothing is invented. Chrome is the shared
// NewShell (HeaderNew + FooterNew). The old /faq page is untouched.
//
// NOTE (flagged): the old page's "ส่งคำถามของคุณ" submit-a-question band +
// modal is intentionally omitted here because the mockup has no such section.

const SCROLL_OFFSET = -108

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const SearchGlyph = () => (
  <svg
    className={styles.searchIcon}
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path
      d="M20 20l-3.2-3.2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

// Turn the faqData object into an ordered list of categories, splitting the
// normal Q&A entries from the two "special" content markers (De minimis /
// Restricted products, used by the inter dataset).
const buildCategories = (faqData) =>
  Object.entries(faqData).map(([name, data]) => {
    const items = []
    let special = null
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'subtitle') return
      if (value === 'De minimis') special = 'deminimis'
      else if (value === 'Restricted products') special = 'restricted'
      else items.push({ title: key, desc: value })
    })
    return { name, subtitle: data.subtitle, items, special }
  })

const FaqNew = ({ type = 'local', title, keywords, description }) => {
  const Router = useRouter()
  const language = Router.locale
  const { t } = useTranslation('faq')

  const faqData = useMemo(
    () => (type === 'inter' ? getFaqInter(language) : getFaqLocal(language)),
    [type, language]
  )
  const categories = useMemo(() => buildCategories(faqData), [faqData])

  const [query, setQuery] = useState('')
  const [activeCat, setActiveCat] = useState(categories[0]?.name || null)

  // First question of the first category starts expanded (matches the mockup).
  const firstKey =
    categories[0]?.items[0] &&
    `${categories[0].name}::${categories[0].items[0].title}`
  const [openKeys, setOpenKeys] = useState(() =>
    firstKey ? { [firstKey]: true } : {}
  )

  const q = query.trim().toLowerCase()

  const filtered = useMemo(() => {
    if (!q) return categories
    return categories
      .map((c) => ({
        ...c,
        special: null, // hide special blocks while searching Q&A titles
        items: c.items.filter((it) => it.title.toLowerCase().includes(q)),
      }))
      .filter((c) => c.items.length > 0)
  }, [categories, q])

  const toggle = (key) =>
    setOpenKeys((prev) => ({ ...prev, [key]: !prev[key] }))
  // While searching every match is expanded so answers are visible; otherwise
  // honour the per-item open state.
  const isOpen = (key) => (q ? true : !!openKeys[key])

  const scrollToCategory = (name) => {
    setActiveCat(name)
    scroller.scrollTo(name, {
      smooth: 'easeInOutQuart',
      duration: 300,
      offset: SCROLL_OFFSET,
    })
  }

  // Track which category is in view to highlight its sidebar card.
  useEffect(() => {
    if (q) return undefined // filtering collapses the section structure
    const nodes = Array.from(document.querySelectorAll('[data-faq-group]'))
    if (!nodes.length) return undefined
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveCat(entry.target.getAttribute('data-faq-group'))
          }
        })
      },
      { rootMargin: '-120px 0px -68% 0px', threshold: 0 }
    )
    nodes.forEach((n) => io.observe(n))
    return () => io.disconnect()
  }, [q, filtered.length])

  let panelSeq = 0

  const renderSpecial = (special) => {
    if (special === 'deminimis') return <DeMinimis />
    if (special === 'restricted') return <RestrictedProducts />
    return null
  }

  const renderAccordion = (category) => (
    <div className={styles.accList}>
      {category.items.map((item) => {
        const key = `${category.name}::${item.title}`
        const open = isOpen(key)
        panelSeq += 1
        const btnId = `faq-btn-${panelSeq}`
        const panelId = `faq-panel-${panelSeq}`
        return (
          <div
            className={clsx(styles.accItem, open && styles.accItemOpen)}
            key={key}
          >
            <button
              type="button"
              id={btnId}
              aria-expanded={open}
              aria-controls={panelId}
              className={clsx(styles.accBtn, open && styles.accBtnOpen)}
              onClick={() => toggle(key)}
            >
              <span className={styles.accQuestion}>{item.title}</span>
              <span
                className={clsx(
                  styles.chevronCircle,
                  open && styles.chevronCircleOpen
                )}
              >
                <ChevronDown />
              </span>
            </button>
            {open && (
              <div
                id={panelId}
                role="region"
                aria-labelledby={btnId}
                className={styles.accPanel}
              >
                <div className={styles.answer}>{item.desc}</div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  return (
    <NewShell
      title={title}
      keywords={keywords}
      description={description}
      headExtra={
        <link
          rel="preload"
          as="image"
          href="/static/img/faq-new/hero.webp"
          type="image/webp"
          fetchpriority="high"
        />
      }
    >
      {/* Hero */}
      <section className={styles.hero}>
        <picture>
          <source srcSet="/static/img/faq-new/hero.webp" type="image/webp" />
          <img
            className={styles.heroImg}
            src="/static/img/faq-new/hero.png"
            alt=""
            width={1920}
            height={777}
            fetchpriority="high"
            decoding="async"
          />
        </picture>
        <div className={styles.heroOverlay}>
          <h1 className={styles.heroHeading}>
            {t('new.heroHeading')} <span>SHIPPOP</span>
          </h1>
          <p className={styles.heroSub}>{t('new.heroSub')}</p>
        </div>
      </section>

      {/* Search box overlapping the hero bottom */}
      <div className={styles.searchWrap}>
        {/* Domestic / International folder tabs — sit ON TOP of and merge into
            the search card's top-left edge (matches the homepage checkprice
            form's tabs). Real next/link so they stay keyboard-focusable. */}
        <div className={styles.faqTabRow} role="tablist" aria-label={t('new.categoriesNav')}>
          <Link href="/faq">
            <a
              role="tab"
              aria-selected={type === 'local'}
              aria-current={type === 'local' ? 'page' : undefined}
              className={clsx(
                styles.faqTab,
                type === 'local' && styles.faqTabActive
              )}
            >
              <img
                src="/static/img/home/checkprice-icon-truck.svg"
                alt=""
                aria-hidden="true"
                width={30}
                height={30}
                decoding="async"
              />
              {t('new.tabLocal')}
            </a>
          </Link>
          <Link href="/inter/faq">
            <a
              role="tab"
              aria-selected={type === 'inter'}
              aria-current={type === 'inter' ? 'page' : undefined}
              className={clsx(
                styles.faqTab,
                type === 'inter' && styles.faqTabActive
              )}
            >
              <img
                src="/static/img/home/checkprice-icon-plane.svg"
                alt=""
                aria-hidden="true"
                width={30}
                height={30}
                decoding="async"
              />
              {t('new.tabInter')}
            </a>
          </Link>
        </div>
        <div className={styles.searchCard}>
          <div className={styles.searchInner}>
            <SearchGlyph />
            <input
              className={styles.searchInput}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('new.searchPlaceholder')}
              aria-label={t('new.searchPlaceholder')}
            />
            {query && (
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => setQuery('')}
                aria-label={t('new.clearSearch')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className={styles.body}>
        <div className={styles.container}>
          <div className={styles.layout}>
            {/* Sticky category sidebar */}
            <nav className={styles.sidebar} aria-label={t('new.categoriesNav')}>
              {categories.map((cat) => {
                const active = activeCat === cat.name
                return (
                  <button
                    type="button"
                    key={cat.name}
                    className={clsx(
                      styles.catCard,
                      active && styles.catCardActive
                    )}
                    aria-current={active ? 'true' : undefined}
                    onClick={() => scrollToCategory(cat.name)}
                  >
                    <p
                      className={clsx(
                        styles.catTitle,
                        active && styles.catTitleActive
                      )}
                    >
                      {cat.name}
                    </p>
                    <p className={styles.catSub}>{cat.subtitle}</p>
                  </button>
                )
              })}
            </nav>

            {/* Grouped accordions */}
            <div className={styles.content}>
              {filtered.length === 0 ? (
                <p className={styles.noResults}>
                  {t('new.noResults', { query })}
                </p>
              ) : (
                filtered.map((cat) => (
                  <ScrollElement
                    name={cat.name}
                    key={cat.name}
                    className={styles.group}
                    data-faq-group={cat.name}
                  >
                    <h2 className={styles.groupHeading}>{cat.name}</h2>
                    {cat.items.length > 0
                      ? renderAccordion(cat)
                      : renderSpecial(cat.special)}
                  </ScrollElement>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </NewShell>
  )
}

export default FaqNew
